import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateClient } from '../../services/clients';
import { createCompany, getCompany } from '../../services/companies';
import CompanySearchSelect from '../CompanySearchSelect';
import ContactsTab from './ContactsTab';
import SiteAddressesTab from './SiteAddressesTab';
import './ContactInfoTab.css';

export default function ContactInfoTab({ client, onUpdate }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: client.name || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    city: client.city || '',
    state: client.state || '',
    zip: client.zip || '',
    notes: client.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [showLinkCompanyModal, setShowLinkCompanyModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [, setLinkingCompany] = useState(false);

  // Load company when client changes
  useEffect(() => {
    const loadCompany = async () => {
      if (client?.company_id) {
        setLoadingCompany(true);
        try {
          const companyData = await getCompany(client.company_id);
          setCompany(companyData);
        } catch (error) {
          console.error('Error loading company:', error);
          setCompany(null);
        } finally {
          setLoadingCompany(false);
        }
      } else {
        setCompany(null);
      }
    };
    loadCompany();
  }, [client?.company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateClient(client.id, formData);
      await onUpdate();
      setEditing(false);
    } catch (error) {
      alert('Failed to update client: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) {
      alert('Please enter a company name');
      return;
    }

    setCreatingCompany(true);
    try {
      const newCompany = await createCompany({ name: newCompanyName.trim() });
      await updateClient(client.id, { company_id: newCompany.id });
      await onUpdate();
      setShowCreateCompanyModal(false);
      setNewCompanyName('');
      setCompany(newCompany);
    } catch (error) {
      alert('Failed to create company: ' + error.message);
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleLinkCompany = async (selectedCompany) => {
    if (!selectedCompany) return;

    setLinkingCompany(true);
    try {
      await updateClient(client.id, { company_id: selectedCompany.id });
      await onUpdate();
      setShowLinkCompanyModal(false);
      setCompany(selectedCompany);
    } catch (error) {
      alert('Failed to link company: ' + error.message);
    } finally {
      setLinkingCompany(false);
    }
  };

  const handleUnlinkCompany = async () => {
    if (!window.confirm('Are you sure you want to unlink this client from the company?')) {
      return;
    }

    try {
      await updateClient(client.id, { company_id: null });
      await onUpdate();
      setCompany(null);
    } catch (error) {
      alert('Failed to unlink company: ' + error.message);
    }
  };

  if (editing) {
    return (
      <div className="contact-info-tab">
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-section">
            <h2>Edit Contact Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  rows="5"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    name: client.name || '',
                    email: client.email || '',
                    phone: client.phone || '',
                    address: client.address || '',
                    city: client.city || '',
                    state: client.state || '',
                    zip: client.zip || '',
                    notes: client.notes || '',
                  });
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="contact-info-tab">
      <div className="tab-header">
        <h2>Contact Information</h2>
        <button className="btn-edit" onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>

      <div className="info-card">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Name</span>
            <span className="info-value">{client.name || <em>Not provided</em>}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email</span>
            <span className="info-value">{client.email ? <a href={`mailto:${client.email}`} className="email-link">{client.email}</a> : <em>Not provided</em>}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Phone</span>
            <span className="info-value">{client.phone ? <a href={`tel:${client.phone}`} className="phone-link">{client.phone}</a> : <em>Not provided</em>}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Address</span>
            <span className="info-value">{client.address || <em>Not provided</em>}</span>
          </div>
          <div className="info-item">
            <span className="info-label">City</span>
            <span className="info-value">{client.city || <em>Not provided</em>}</span>
          </div>
          <div className="info-item">
            <span className="info-label">State</span>
            <span className="info-value">{client.state || <em>Not provided</em>}</span>
          </div>
          <div className="info-item">
            <span className="info-label">ZIP Code</span>
            <span className="info-value">{client.zip || <em>Not provided</em>}</span>
          </div>
        </div>
      </div>

      {client.notes && (
        <div className="info-card">
          <h3>Notes</h3>
          <p className="notes-content">{client.notes}</p>
        </div>
      )}

      <div className="info-card">
        <h3>Additional Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Client ID</span>
            <span className="info-value">{client.id}</span>
          </div>
          {client.created_at && (
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">
                {new Date(client.created_at).toLocaleDateString()}
              </span>
            </div>
          )}
          {client.updated_at && (
            <div className="info-item">
              <span className="info-label">Last Updated</span>
              <span className="info-value">
                {new Date(client.updated_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="info-card">
        <ContactsTab clientId={client.id} />
      </div>

      <div className="info-card">
        <SiteAddressesTab clientId={client.id} client={client} />
      </div>

      {/* Company Section */}
      <div className="info-card">
        <div className="tab-header">
          <h3>Company</h3>
        </div>
        {loadingCompany ? (
          <div className="loading-state">Loading company...</div>
        ) : company ? (
          <div className="company-info">
            <div className="info-item">
              <span className="info-label">Company Name</span>
              <span className="info-value">{company.name}</span>
            </div>
            <div className="company-actions">
              <button
                className="btn-primary"
                onClick={() => navigate(`/companies/${company.id}`)}
              >
                View Company
              </button>
              <button
                className="btn-secondary"
                onClick={handleUnlinkCompany}
              >
                Unlink Company
              </button>
            </div>
          </div>
        ) : (
          <div className="company-actions">
            <button
              className="btn-primary"
              onClick={() => setShowCreateCompanyModal(true)}
            >
              Create Company
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowLinkCompanyModal(true)}
            >
              Link to Existing Company
            </button>
          </div>
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateCompanyModal && (
        <div className="modal-overlay" onClick={() => setShowCreateCompanyModal(false)}>
          <div className="site-address-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Company</h2>
              <button className="btn-close" onClick={() => setShowCreateCompanyModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateCompany}>
              <div className="site-address-modal-body">
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    required
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    autoFocus
                  />
                </div>
              </div>
              <div className="site-address-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowCreateCompanyModal(false);
                    setNewCompanyName('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={creatingCompany}>
                  {creatingCompany ? 'Creating...' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link Company Modal */}
      {showLinkCompanyModal && (
        <div className="modal-overlay" onClick={() => setShowLinkCompanyModal(false)}>
          <div className="site-address-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link to Existing Company</h2>
              <button className="btn-close" onClick={() => setShowLinkCompanyModal(false)}>×</button>
            </div>
            <div className="site-address-modal-body">
              <div className="form-group">
                <label>Search for Company</label>
                <CompanySearchSelect
                  onSelect={handleLinkCompany}
                  placeholder="Type to search for a company..."
                />
              </div>
            </div>
            <div className="site-address-modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowLinkCompanyModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
